import { format, isAfter, isSameDay } from 'date-fns'
import { Action, Dispatch } from 'redux'
import { db, FirebaseDocRefType, FirebaseTimestamp, FirebaseWriteBatch } from 'firebase/index'
import { getEarliestOrderStartTime } from 'lib/businessHour'
import { DaysOfWeek } from 'lib/daysOfWeek'
import { parseHoursToDeadline, parseTimeToDate, parseToDate } from 'lib/helper'
import { BusinessTimeType } from 'types/store'
import { StockType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { State } from 'index'
const daysOfWeek = new DaysOfWeek()

const updatePreorderDeadline = ({
  orderStartTime,
  storeRef,
}: {
  orderStartTime: BusinessTimeType
  storeRef: FirebaseDocRefType
}) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    const specialHolidays = getState().calendar.specialHolidays

    const preorderStocks = await fetchPreorderStocks(storeRef)
    const preorderSchedules = await fetchPreorderSchedules(storeRef)

    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0

    for (const { stock_ref: ref, ...stock } of preorderStocks) {
      if (!ref) continue
      const deadline = stock.deadline.toDate()
      const date = parseToDate(stock.date)
      const dayOfWeek = daysOfWeek.getDayOfWeek(date, specialHolidays)
      const orderStartDate = parseTimeToDate(orderStartTime[dayOfWeek] ?? '00:00', date)
      // 締め切り時刻が当日でなければ更新しない
      if (!isSameDay(deadline, date)) continue
      // 変更後のオーダー開始時刻が締め切り時刻よりも後であれば更新しない
      if (isAfter(orderStartDate, deadline)) continue

      const nextValue: Pick<StockType, 'deadline'> = {
        deadline: FirebaseTimestamp.fromDate(orderStartDate),
      }
      batchArray[batchIndex].update(ref, nextValue)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    for (const { schedule_ref: ref, ...schedule } of preorderSchedules) {
      if (!ref) continue
      // 締め切り時刻が当日でなければ更新しない
      if (schedule.hours_to_deadline > 0) continue
      const earliestOrderStartTime = getEarliestOrderStartTime(schedule.days_of_week, orderStartTime)
      const orderStartTimeHours = parseHoursToDeadline('0', earliestOrderStartTime)
      // 変更後のオーダー開始時刻が締め切り時刻よりも後であれば更新しない
      if (orderStartTimeHours < schedule.hours_to_deadline) continue

      const nextValue: Pick<ScheduleType, 'hours_to_deadline'> = {
        hours_to_deadline: orderStartTimeHours,
      }
      batchArray[batchIndex].update(ref, nextValue)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    try {
      await batchArray.forEach(async (batch) => await batch.commit())
      return
    } catch (e) {
      console.error(e)
      return
    }
  }
}

// 今日以降に販売する事前予約商品の在庫を取得
const fetchPreorderStocks = async (storeRef: FirebaseDocRefType): Promise<StockType[]> => {
  const date = format(new Date(), 'yyyyMMdd')
  return storeRef
    .collection('store_stocks')
    .where('type', 'in', ['preorder'])
    .orderBy('date', 'asc')
    .startAt(date)
    .get()
    .then((snapshots) =>
      snapshots.docs.map((snapshot) => {
        const data = snapshot.data() as StockType
        data.stock_ref = snapshot.ref
        return data
      })
    )
    .catch((error) => {
      console.error(error)
      return []
    })
}

const fetchPreorderSchedules = async (storeRef: FirebaseDocRefType): Promise<ScheduleType[]> => {
  return storeRef
    .collection('store_schedules')
    .where('type', 'in', ['preorder'])
    .orderBy('bread_ref', 'asc')
    .get()
    .then((snapshots) =>
      snapshots.docs.map((snapshot) => {
        const data = snapshot.data() as ScheduleType
        data.schedule_ref = snapshot.ref
        return data
      })
    )
    .catch((error) => {
      console.error(error)
      return []
    })
}

export default updatePreorderDeadline
